import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import ScrollReveal from "@/components/ScrollReveal";
import { ArrowRight } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1758518726869-01aff69a56e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzc2MDc1MTMwfDA&ixlib=rb-4.1.0&q=85";

export default function About() {
  const { t } = useLanguage();
  const a = t.aboutPage;

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-24 sm:pb-32 bg-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <ScrollReveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {a.hero.overline}
            </p>
            <h1 data-testid="about-page-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl">
              {a.hero.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/60 max-w-2xl">
              {a.hero.description}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Story */}
      <section data-testid="about-story" className="py-24 sm:py-32 bg-[#333333]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-6">
              <ScrollReveal>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-8">
                  {a.story.title}
                </h2>
                <div className="space-y-6">
                  {a.story.paragraphs.map((p, i) => (
                    <p key={i} className="text-white/60 leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              </ScrollReveal>
            </div>
            <div className="lg:col-span-6">
              <ScrollReveal delay={200}>
                <div className="relative">
                  <div className="absolute -inset-4 border border-[#E8A400]/20 -z-10" />
                  <img
                    src={ABOUT_IMG}
                    alt="CREE8 Solutions"
                    className="w-full aspect-[4/3] object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="py-24 sm:py-32 bg-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <ScrollReveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {a.values.overline}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-16">
              {a.values.title}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {a.values.items.map((item, i) => (
              <ScrollReveal key={i} delay={i * 100}>
                <div data-testid={`value-${i}`} className="bg-[#2A2A2A] p-8 lg:p-10">
                  <div className="flex items-start gap-4">
                    <span className="text-3xl font-bold text-[#E8A400]/20 tracking-tighter shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold tracking-tight mb-3">
                        {item.title}
                      </h3>
                      <p className="text-sm text-white/50 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#333333]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 text-center">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter mb-6">
              {t.finalCta.title}
            </h2>
            <p className="text-base text-white/60 max-w-lg mx-auto mb-10">
              {t.finalCta.description}
            </p>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="inline-flex items-center gap-2 bg-[#E8A400] text-[#1A1A1A] font-semibold px-10 py-4 hover:bg-[#C68C00] transition-colors duration-300 rounded-sm text-sm"
            >
              {t.finalCta.cta}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
